import React from "react";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import ReportLayout from "../components/ReportLayout";
import InitiativeSpotlight from "../components/InitiativeSpotlight";
import {
    Meta,
    Stat,
    Person,
    StatusPill,
    KpiBlock,
    CrossFunctional,
} from "../components/ReportPrimitives";

// Edit this when you publish a new version of the Business report.
// Each report has its own LAST_UPDATED constant — they update independently.
const LAST_UPDATED = "May 20, 2026";

/* -------------------- DATA -------------------- */

const NAV_ITEMS = [
    { href: "#summary", label: "Summary" },
    { href: "#progress", label: "Progress" },
    { href: "#pillars", label: "Pillars" },
    { href: "#priorities", label: "Forward" },
    { href: "#kpis", label: "KPIs" },
    { href: "#capacity", label: "Capacity" },
    { href: "#coordination", label: "Coordination" },
    { href: "#risks", label: "Risks" },
];

/* Build journal — March (Foundation) + April (Activation). */
const BUILD_JOURNAL = [
    {
        month: "March 2026",
        title: "Foundation",
        eyebrow: "Month 1",
        infrastructure: [
            { name: "HubSpot CRM Architecture", status: "Done" },
            { name: "30-Day HubSpot Execution Tracker", status: "Done" },
        ],
        commercial: [
            { name: "Revenue Department Architecture", status: "Done" },
            { name: "Sales Cycle & Pipeline Definition (8-stage)", status: "Done" },
            { name: "Partnerships Framework (3-tier channel model)", status: "Done" },
        ],
        visibility: [
            { name: "Master P&L Workbook (15-page reconciled)", status: "Done" },
        ],
    },
    {
        month: "April 2026",
        title: "Activation",
        eyebrow: "Month 2",
        infrastructure: [
            { name: "HubSpot CRM Go-Live", status: "In Progress" },
        ],
        commercial: [
            { name: "Revenue team workshops", status: "Done" },
            { name: "GTM Plan refinement", status: "In Progress" },
            { name: "Sales Compensation Model", status: "Done" },
        ],
        visibility: [
            { name: "Investor Teaser Deck", status: "Paused" },
            { name: "Investor KPI Dashboard", status: "Paused" },
            { name: "Investor Reporting Framework", status: "Paused" },
        ],
    },
];

/* Strategic pillars — 3 cards. */
const PILLARS = [
    {
        n: "01",
        name: "CRM & Revenue Operations",
        body:
            "HubSpot CRM as the central system of record for sales, pipeline, and rep-level KPI tracking. Foundation built — go-live and adoption define May execution.",
    },
    {
        n: "02",
        name: "GTM Strategy & Channels",
        body:
            "Three-tier channel model and refined GTM plan. CCHBC market activation, Germany & Italy team build, and partnership engagement drive Q2–Q3 expansion.",
    },
    {
        n: "03",
        name: "Revenue & Compensation",
        body:
            "5M EUR 2026 target, defined quarterly split, and performance-linked compensation framework cascading from Sales Heads to rep level.",
    },
];

/* Forward plan priorities — May–July 2026.
   `crossRef` (optional) renders an inline link to a related section in
   another report — gives stakeholders a one-click jump between the
   sister reports for the same workstream. */
const PRIORITIES = [
    {
        n: "01",
        title: "HubSpot Go-Live + Data Import",
        tag: "May · Critical",
        body:
            "Complete CRM go-live and historic data migration. Unblocks rep-level KPI tracking, GTM plan updates, and downstream reporting. Owner: Marko Pavičić (RevOps Head).",
        crossRef: {
            href: "/product-it#coordination",
            label: "PIT · HubSpot × Digital Onboarding milestone",
        },
    },
    {
        n: "02",
        title: "GTM Plan Update",
        tag: "May · Primary",
        body:
            "Final GTM plan publication post-HubSpot go-live, with market scope, team structure, and roadmap aligned to revenue targets. Owner: CBO + Sales Heads.",
    },
    {
        n: "03",
        title: "CCHBC Market Activation",
        tag: "June · Strategic",
        body:
            "Activate CCHBC markets with standardized commercial motion. Coordination with Product & IT on CCHBC standardization milestone. Owner: Ognjen Zlački + Srećko Knezović.",
        crossRef: {
            href: "/product-it#coordination",
            label: "PIT · CCHBC standardization (Warsaw, May 19)",
        },
    },
    {
        n: "04",
        title: "Germany & Italy Team Hires",
        tag: "June · Build",
        body:
            "Build out regional teams to support international expansion. CS Lead hiring underway via external contractor (Robert Malekin).",
    },
    {
        n: "05",
        title: "Investor Materials Reactivation",
        tag: "June · Pending",
        body:
            "Resume Investor Teaser Deck, KPI Dashboard, and Reporting Framework. Currently paused awaiting external company input — un-pause action required.",
    },
    {
        n: "06",
        title: "Q3 Push: 1.5M EUR",
        tag: "July · Revenue",
        body:
            "Push quarter for revenue activation. All commercial systems, teams, and markets must be operational. KPI cadence and country-by-country reporting live.",
    },
];

/* KPIs — Business focused. */
const KPIS_REVENUE = [
    {
        n: "01",
        name: "2026 Annual Revenue",
        definition:
            "Total revenue achieved against the 5M EUR 2026 target across all markets and product lines.",
        target: "5M EUR",
        status: "Baseline tracking via HubSpot post go-live.",
        focus: "Q1 actual vs plan reconciliation.",
    },
    {
        n: "02",
        name: "Quarterly Pacing",
        definition:
            "Revenue pacing across Q1–Q4 to ensure 1.5M EUR push in Q3 (peak quarter) is fully resourced.",
        target: "Q3 1.5M",
        status: "Forecast in build, locked post-GTM update.",
        focus: "Q2 ramp-up vs Q3 push readiness.",
    },
    {
        n: "03",
        name: "Pipeline Coverage",
        definition:
            "Active pipeline value (8-stage) as a multiple of remaining quarterly revenue target.",
        target: "3x",
        status: "Pipeline structure defined; data flows post-go-live.",
        focus: "Coverage baseline by end of May.",
    },
    {
        n: "04",
        name: "Win Rate & Cycle Time",
        definition:
            "% of qualified opportunities won and median time from qualified to closed-won.",
        target: "TBD",
        status: "Tracking definition in progress.",
        focus: "Establish baselines from first 90 days post go-live.",
    },
    {
        n: "05",
        name: "Rep Quota Attainment",
        definition:
            "% of sales reps achieving quarterly quota — cascaded from Sales Head targets.",
        target: "TBD",
        status: "Compensation model in place; tracking gates on go-live.",
        focus: "First quarterly review July 2026.",
    },
];

const KPIS_GTM = [
    {
        n: "01",
        name: "Active Markets",
        definition:
            "Number of markets with operational commercial motion (sales, partner, customer success).",
        target: "TBD",
        status: "Croatia live; CCHBC and DE/IT in activation.",
        focus: "CCHBC pilot live in June.",
    },
    {
        n: "02",
        name: "Partner Activations",
        definition:
            "Active partners across the 3-tier channel model contributing measurable pipeline.",
        target: "TBD",
        status: "Framework defined; partner onboarding sequencing.",
        focus: "First-tier partners signed by July.",
    },
    {
        n: "03",
        name: "GTM Hire Velocity",
        definition:
            "% of planned commercial roles filled (Germany, Italy, CS Lead).",
        target: "TBD",
        status: "CS Lead hiring underway via contractor.",
        focus: "Country lead hires complete by Q3.",
    },
    {
        n: "04",
        name: "CRM Adoption",
        definition:
            "% of sales activity logged in HubSpot vs. shadow tools (spreadsheets, email).",
        target: "TBD",
        status: "Baseline post go-live.",
        focus: "Adoption review 30 / 60 / 90 days post-launch.",
    },
];

/* Risks / Open Items. */
const RISKS = [
    "HubSpot Go-Live delay — cascading impact on rep-level KPIs, GTM plan dependencies, and reporting cadence.",
    "Investor materials paused — Teaser Deck, KPI Dashboard, and Reporting Framework awaiting external company input.",
    "2 open items carried forward from April — to be reconciled in May.",
    "External CS Lead hiring dependency — risk of misalignment with internal team build.",
    "CCHBC market activation cadence — needs alignment ASAP with Product & IT and CCHBC team.",
];

/* Cross-team dependencies. */
const DEPENDENCIES = [
    {
        team: "Product & IT",
        owner: "Karlo Duđerija · CPO",
        items: [
            "HubSpot ↔ Digital Onboarding integration (June 1, 2026)",
            "CCHBC standardization milestone (Warsaw workshop, May 19)",
            "First 200 Clients · Viva → Adyen migration",
            "Adyen integration kickoff (May 20)",
        ],
    },
    {
        team: "Operations",
        owner: "Tanja Krasić · COO",
        items: [
            "GTM activation alignment with Operations readiness",
            "New market launches require operational readiness checklist",
            "Partner onboarding ↔ Ops process handoffs",
        ],
    },
    {
        team: "Finance / CFO",
        owner: "CFO + Master P&L",
        items: [
            "Master P&L Workbook reconciliation (15-page model)",
            "Q3 push quarter financial forecast",
            "Investor Reporting Framework (paused)",
        ],
    },
];

/* -------------------- COMPONENT -------------------- */

const BusinessReport = () => {
    return (
        <ReportLayout navItems={NAV_ITEMS} lastUpdated={LAST_UPDATED} reportType="business">
            {/* HERO */}
            <section
                id="top"
                className="relative bg-black text-white overflow-hidden"
                data-testid="hero-section"
            >
                <div className="absolute inset-0 fora-pattern opacity-90" />
                <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-[var(--fora-red-1)] opacity-25 blur-3xl" />
                <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 pt-20 pb-24 lg:pt-28 lg:pb-32">
                    <div className="flex items-center gap-3 rise">
                        <span className="red-dot" />
                        <span className="section-num text-white/80">
                            Business Progress · Confidential
                        </span>
                    </div>

                    <h1
                        className="rise d1 mt-6 font-[800] tracking-[-0.04em] leading-[0.95] text-4xl sm:text-6xl lg:text-[88px]"
                        data-testid="hero-title"
                    >
                        Commercial and{" "}
                        <br />
                        Business Progress.
                    </h1>

                    <p className="rise d2 mt-6 max-w-2xl text-sm sm:text-lg text-white/80 leading-relaxed">
                        Reporting period{" "}
                        <span className="text-white font-semibold">
                            March – April 2026
                        </span>
                        . Forward view{" "}
                        <span className="text-white font-semibold">
                            May – July 2026
                        </span>
                        . Foundation and Activation phases complete — pivoting
                        from build to GTM delivery.
                    </p>

                    <div className="rise d3 mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-10 max-w-4xl">
                        <Meta
                            label="Submitted to"
                            value="Ivan Brkić"
                            sub="Managing Director"
                        />
                        <Meta
                            label="Reporting"
                            value="Mar – Apr 2026"
                            sub="Foundation + Activation"
                        />
                        <Meta
                            label="Forward"
                            value="May – Jul 2026"
                            sub="GTM Delivery push"
                        />
                        <Meta
                            label="Owner"
                            value="Marko Erkić"
                            sub=""
                        />
                    </div>

                    <div className="rise d4 mt-12 flex flex-wrap items-center gap-3">
                        <span className="chip">
                            <span className="red-dot" /> CBO · Marko Erkić
                        </span>
                        <span className="chip">
                            <span className="red-dot" /> Marko Pavičić · RevOps Head
                        </span>
                        <span className="chip">
                            <span className="red-dot" /> Petar Kostić · ExYu Sales Head
                        </span>
                        <span className="chip">
                            <span className="red-dot" /> Ognjen Zlački · Global Sales Head
                        </span>
                        <span className="chip">
                            <span className="red-dot" /> Srećko Knezović · Partnership Head
                        </span>
                    </div>
                </div>

                {/* Marquee strip */}
                <div className="fora-strip relative">
                    <div className="fora-strip-track py-3">
                        {[
                            "HUBSPOT GO-LIVE",
                            "GTM DELIVERY",
                            "5M EUR · 2026 TARGET",
                            "CCHBC MARKET ACTIVATION",
                            "Q3 PUSH QUARTER",
                            "GERMANY · ITALY",
                            "PARTNER FRAMEWORK",
                        ]
                            .concat([
                                "HUBSPOT GO-LIVE",
                                "GTM DELIVERY",
                                "5M EUR · 2026 TARGET",
                                "CCHBC MARKET ACTIVATION",
                                "Q3 PUSH QUARTER",
                                "GERMANY · ITALY",
                                "PARTNER FRAMEWORK",
                            ])
                            .map((s, i) => (
                                <React.Fragment key={i}>
                                    <span>{s}</span>
                                    <span className="pip" />
                                </React.Fragment>
                            ))}
                    </div>
                </div>
            </section>

            {/* CROSS-CUTTING INITIATIVE SPOTLIGHT */}
            <InitiativeSpotlight />

            {/* 01 — EXECUTIVE SUMMARY */}
            <section
                id="summary"
                className="bg-white"
                data-testid="summary-section"
            >
                <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
                    <div className="grid grid-cols-12 gap-8">
                        <div className="col-span-12 lg:col-span-4">
                            <div className="flex items-center gap-3">
                                <span className="red-dot" />
                                <span className="section-num">
                                    01 — Summary
                                </span>
                            </div>
                            <h2 className="mt-5 font-[800] tracking-[-0.03em] leading-[1] text-4xl sm:text-5xl">
                                Executive
                                <br />
                                summary.
                            </h2>
                        </div>
                        <div
                            className="col-span-12 lg:col-span-8"
                            data-testid="summary-body"
                        >
                            <div className="space-y-5 text-sm sm:text-base leading-relaxed text-[var(--fora-ink)]/85 break-words">
                                <p>
                                    During March and April, the{" "}
                                    <strong>Commercial Build</strong> advanced
                                    through{" "}
                                    <strong>Foundation</strong> and{" "}
                                    <strong>Activation</strong> phases —
                                    establishing CRM architecture, sales
                                    pipeline definition, GTM strategy,
                                    partnerships framework, and revenue
                                    targeting.
                                </p>
                                <p>
                                    March delivered the{" "}
                                    <strong>HubSpot CRM Architecture</strong>,
                                    the{" "}
                                    <strong>
                                        Revenue Department Architecture
                                    </strong>
                                    , an{" "}
                                    <strong>8-stage sales pipeline</strong>, a{" "}
                                    <strong>3-tier channel partnership model</strong>
                                    , and a{" "}
                                    <strong>15-page Master P&amp;L Workbook</strong>{" "}
                                    reconciling commercial assumptions.
                                </p>
                                <p>
                                    April pivoted into Activation —{" "}
                                    <strong>HubSpot Go-Live</strong> began
                                    (still in progress), revenue team workshops
                                    ran, the GTM plan refinement advanced, and
                                    a{" "}
                                    <strong>Sales Compensation Model</strong>{" "}
                                    cascading from Sales Head targets was
                                    locked. Investor materials (Teaser Deck,
                                    KPI Dashboard, Reporting Framework) are
                                    paused awaiting external company input.
                                </p>
                                <p>
                                    The next quarter pivots from build to{" "}
                                    <strong>GTM delivery</strong>. The central
                                    dependency is{" "}
                                    <strong>HubSpot Go-Live</strong> — without
                                    full operationalization, rep-level KPI
                                    tracking, GTM plan finalization, and
                                    reporting cadence cannot complete. Q3 is
                                    the push quarter with a{" "}
                                    <strong>1.5M EUR target</strong>.
                                </p>
                            </div>

                            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
                                <Stat n="23" label="Deliverables produced" />
                                <Stat n="2" label="Open items carried" />
                                <Stat n="5M" label="EUR 2026 target" />
                                <Stat n="Q3" label="Push quarter · 1.5M" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 02 — PROGRESS / BUILD JOURNAL */}
            <section
                id="progress"
                className="bg-[var(--fora-gray)]/40 border-y border-[var(--fora-gray)]"
                data-testid="progress-section"
            >
                <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
                    <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
                        <div>
                            <div className="flex items-center gap-3">
                                <span className="red-dot" />
                                <span className="section-num">
                                    02 — Progress
                                </span>
                            </div>
                            <h2 className="mt-5 font-[800] tracking-[-0.03em] leading-[1] text-4xl sm:text-5xl">
                                Build journal
                                <br />
                                March → April.
                            </h2>
                        </div>
                        <p className="max-w-md text-[var(--fora-muted)] text-base">
                            Two phases of the Commercial Build delivered across
                            three workstreams — Infrastructure, Commercial, and
                            Visibility.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                        {BUILD_JOURNAL.map((m) => (
                            <div
                                key={m.month}
                                className="fora-card"
                                data-testid={`journal-${m.eyebrow.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                                <div className="flex items-baseline justify-between">
                                    <div>
                                        <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[var(--fora-red-1)]">
                                            {m.eyebrow}
                                        </div>
                                        <h3 className="mt-2 font-[800] text-2xl sm:text-3xl tracking-[-0.02em]">
                                            {m.month}
                                        </h3>
                                    </div>
                                    <span className="text-[10px] font-bold tracking-[0.22em] uppercase text-[var(--fora-muted)]">
                                        {m.title}
                                    </span>
                                </div>

                                <JournalGroup
                                    label="Infrastructure"
                                    items={m.infrastructure}
                                />
                                <JournalGroup
                                    label="Commercial"
                                    items={m.commercial}
                                />
                                <JournalGroup
                                    label="Visibility"
                                    items={m.visibility}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 03 — STRATEGIC PILLARS */}
            <section
                id="pillars"
                className="bg-white"
                data-testid="priorities-section"
            >
                <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
                    <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
                        <div>
                            <div className="flex items-center gap-3">
                                <span className="red-dot" />
                                <span className="section-num">
                                    03 — Strategic Pillars
                                </span>
                            </div>
                            <h2 className="mt-5 font-[800] tracking-[-0.03em] leading-[1] text-4xl sm:text-5xl">
                                Three pillars
                                <br />
                                anchor the build.
                            </h2>
                        </div>
                    </div>

                    <div
                        className="grid grid-cols-1 md:grid-cols-3 gap-5"
                        data-testid="priorities-grid"
                    >
                        {PILLARS.map((p) => (
                            <article
                                key={p.n}
                                className="fora-card"
                                data-testid={`priority-card-${p.n}`}
                            >
                                <div className="pillar-num">{p.n}</div>
                                <h3 className="mt-4 font-[700] text-xl sm:text-2xl tracking-[-0.02em] leading-snug">
                                    {p.name}
                                </h3>
                                <p className="mt-3 text-[var(--fora-muted)] text-sm leading-relaxed">
                                    {p.body}
                                </p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* 04 — FORWARD PRIORITIES */}
            <section
                id="priorities"
                className="bg-[var(--fora-gray)]/40 border-y border-[var(--fora-gray)]"
                data-testid="forward-section"
            >
                <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
                    <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
                        <div>
                            <div className="flex items-center gap-3">
                                <span className="red-dot" />
                                <span className="section-num">
                                    04 — Forward Priorities
                                </span>
                            </div>
                            <h2 className="mt-5 font-[800] tracking-[-0.03em] leading-[1] text-4xl sm:text-5xl">
                                May — July.
                                <br />
                                GTM delivery.
                            </h2>
                        </div>
                        <p className="max-w-md text-[var(--fora-muted)] text-base">
                            Six priorities define the next quarter — pivoting
                            from build to delivery.
                        </p>
                    </div>

                    <div
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
                        data-testid="forward-grid"
                    >
                        {PRIORITIES.map((p) => (
                            <article
                                key={p.n}
                                className="fora-card flex flex-col"
                                data-testid={`forward-card-${p.n}`}
                            >
                                <div className="flex items-baseline justify-between">
                                    <div className="pillar-num">{p.n}</div>
                                    <span className="text-[10px] font-bold tracking-[0.22em] uppercase text-[var(--fora-red-1)]">
                                        {p.tag}
                                    </span>
                                </div>
                                <h3 className="mt-4 font-[700] text-lg sm:text-xl tracking-[-0.02em] leading-snug">
                                    {p.title}
                                    {p.crossRef && (
                                        <>
                                            {" "}
                                            <CrossFunctional size="xs" />
                                        </>
                                    )}
                                </h3>
                                <p className="mt-3 text-[var(--fora-muted)] text-sm leading-relaxed flex-1">
                                    {p.body}
                                </p>
                                {p.crossRef && (
                                    <Link
                                        to={p.crossRef.href}
                                        className="no-print mt-4 pt-3 border-t border-[var(--fora-gray)] inline-flex items-center justify-between gap-2 text-[10px] font-bold tracking-[0.18em] uppercase text-[var(--fora-ink)]/65 hover:text-[var(--fora-red-1)] transition-colors group"
                                        data-testid={`forward-card-${p.n}-crossref`}
                                    >
                                        <span className="flex-1 normal-case tracking-normal text-xs font-semibold leading-snug">
                                            ↗ {p.crossRef.label}
                                        </span>
                                        <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform shrink-0" />
                                    </Link>
                                )}
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* 05 — KPIs */}
            <section
                id="kpis"
                className="bg-black text-white relative overflow-hidden"
                data-testid="kpis-section"
            >
                <div className="absolute inset-0 fora-pattern opacity-70" />
                <div className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full bg-[var(--fora-red-2)] opacity-20 blur-3xl" />
                <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
                    <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
                        <div>
                            <div className="flex items-center gap-3">
                                <span className="red-dot" />
                                <span className="section-num text-white/80">
                                    05 — KPI Dashboard
                                </span>
                            </div>
                            <h2 className="mt-5 font-[800] tracking-[-0.03em] leading-[1] text-4xl sm:text-5xl">
                                How we are
                                <br />
                                measuring it.
                            </h2>
                        </div>
                        <p className="max-w-md text-white/55 text-base">
                            Two KPI families — Revenue & Pipeline, and GTM
                            Activation. Most rep-level KPIs gate on HubSpot
                            Go-Live.
                        </p>
                    </div>

                    <KpiBlock
                        eyebrow="A — Revenue & Pipeline"
                        title="Revenue KPIs"
                        subtitle="Quota · pipeline · cycle time · attainment."
                        rows={KPIS_REVENUE}
                        testIdPrefix="kpi-revenue"
                    />

                    <div className="mt-16">
                        <KpiBlock
                            eyebrow="B — GTM"
                            title="GTM KPIs"
                            subtitle="Markets · partners · hires · CRM adoption."
                            rows={KPIS_GTM}
                            testIdPrefix="kpi-gtm"
                        />
                    </div>
                </div>
            </section>

            {/* 06 — CAPACITY / HIRING */}
            <section
                id="capacity"
                className="bg-white border-b border-[var(--fora-gray)]"
                data-testid="capacity-section"
            >
                <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
                    <div className="grid grid-cols-12 gap-8 items-start">
                        <div className="col-span-12 lg:col-span-5">
                            <div className="flex items-center gap-3">
                                <span className="red-dot" />
                                <span className="section-num">
                                    06 — Capacity
                                </span>
                            </div>
                            <h2 className="mt-5 font-[800] tracking-[-0.03em] leading-[1] text-4xl sm:text-5xl">
                                Building the
                                <br />
                                commercial team.
                            </h2>
                            <p className="mt-6 text-[var(--fora-muted)] text-base leading-relaxed">
                                Regional team build for Germany and Italy,
                                Customer Success leadership, and country leads
                                for Q3 push readiness.
                            </p>
                        </div>
                        <div className="col-span-12 lg:col-span-7 grid sm:grid-cols-2 gap-4">
                            <div
                                className="fora-card"
                                data-testid="staffing-team-build"
                            >
                                <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[var(--fora-red-1)]">
                                    Team Build
                                </div>
                                <div className="mt-2 text-3xl font-[800] tracking-[-0.02em]">
                                    Germany & Italy
                                </div>
                                <p className="mt-3 text-sm text-[var(--fora-muted)] leading-relaxed">
                                    Regional commercial teams in active hiring
                                    — country leads, sales reps, and partner
                                    managers. Targeting Q2–Q3 ramp.
                                </p>
                            </div>
                            <div
                                className="fora-card"
                                data-testid="staffing-cs-lead"
                            >
                                <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[var(--fora-red-1)]">
                                    External Hire
                                </div>
                                <div className="mt-2 text-3xl font-[800] tracking-[-0.02em]">
                                    CS Lead
                                </div>
                                <p className="mt-3 text-sm text-[var(--fora-muted)] leading-relaxed">
                                    Customer Success leadership engagement
                                    underway via external contractor (Robert
                                    Malekin).
                                </p>
                            </div>
                            <div
                                className="fora-card sm:col-span-2"
                                data-testid="staffing-sales-heads"
                            >
                                <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[var(--fora-red-1)]">
                                    2026 Commercial Leadership
                                </div>
                                <div className="mt-2 text-xl sm:text-2xl font-[800] tracking-[-0.02em] leading-tight">
                                    Marko Pavičić · Petar Kostić · Ognjen
                                    Zlački · Srećko Knezović
                                </div>
                                <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
                                    <div>
                                        <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[var(--fora-muted)]">
                                            Marko Pavičić
                                        </div>
                                        <div className="mt-0.5 text-[var(--fora-ink)]/80">
                                            RevOps Head
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[var(--fora-muted)]">
                                            Petar Kostić
                                        </div>
                                        <div className="mt-0.5 text-[var(--fora-ink)]/80">
                                            ExYu Sales Head
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[var(--fora-muted)]">
                                            Ognjen Zlački
                                        </div>
                                        <div className="mt-0.5 text-[var(--fora-ink)]/80">
                                            Global Sales Head
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[var(--fora-muted)]">
                                            Srećko Knezović
                                        </div>
                                        <div className="mt-0.5 text-[var(--fora-ink)]/80">
                                            Partnership Head
                                        </div>
                                    </div>
                                </div>
                                <p className="mt-4 text-sm text-[var(--fora-muted)] leading-relaxed">
                                    Revenue ownership cascaded across RevOps,
                                    regional (ExYu), global, and partnership
                                    lanes. Quotas, comp plans, and territories
                                    locked. Rep-level cascade gates on HubSpot
                                    Go-Live (RevOps Manager owner).
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 07 — CROSS-TEAM COORDINATION */}
            <section
                id="coordination"
                className="bg-white border-b border-[var(--fora-gray)]"
                data-testid="coordination-section"
            >
                <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
                    <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
                        <div>
                            <div className="flex items-center gap-3">
                                <span className="red-dot" />
                                <span className="section-num">
                                    07 — Coordination
                                </span>
                            </div>
                            <h2 className="mt-5 font-[800] tracking-[-0.03em] leading-[1] text-4xl sm:text-5xl">
                                Working with
                                <br />
                                Product, Ops &amp; Finance.
                            </h2>
                        </div>
                        <p className="max-w-md text-[var(--fora-muted)] text-base">
                            Commercial delivery in May–July depends on close
                            alignment with Product & IT, Operations, and
                            Finance.
                        </p>
                    </div>

                    <div
                        className="grid grid-cols-1 md:grid-cols-3 gap-5"
                        data-testid="dependencies-grid"
                    >
                        {DEPENDENCIES.map((d) => (
                            <div
                                key={d.team}
                                className="border border-[var(--fora-ink)] p-5"
                                data-testid={`dependency-${d.team.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            >
                                <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[var(--fora-red-1)]">
                                    {d.team}
                                </div>
                                <div className="mt-2 text-base font-[700] tracking-[-0.01em]">
                                    {d.owner}
                                </div>
                                <ul className="mt-4 space-y-2">
                                    {d.items.map((it, i) => (
                                        <li
                                            key={i}
                                            className="flex gap-2 text-sm text-[var(--fora-ink)]/80 leading-snug"
                                        >
                                            <span className="red-dot mt-[7px] shrink-0" />
                                            <span>{it}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 08 — RISKS / OPEN ITEMS */}
            <section
                id="risks"
                className="bg-[var(--fora-gray)]/40 border-b border-[var(--fora-gray)]"
                data-testid="risks-section"
            >
                <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
                    <div className="grid grid-cols-12 gap-8">
                        <div className="col-span-12 lg:col-span-4">
                            <div className="flex items-center gap-3">
                                <span className="red-dot" />
                                <span className="section-num">
                                    08 — Risks
                                </span>
                            </div>
                            <h2 className="mt-5 font-[800] tracking-[-0.03em] leading-[1] text-4xl sm:text-5xl">
                                Risks &amp;
                                <br />
                                dependencies.
                            </h2>
                        </div>
                        <div className="col-span-12 lg:col-span-8">
                            <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[var(--fora-red-1)] mb-4">
                                Watch list
                            </div>
                            <ul className="space-y-3">
                                {RISKS.map((r, i) => (
                                    <li
                                        key={i}
                                        className="flex gap-3 text-base leading-relaxed text-[var(--fora-ink)]/85"
                                    >
                                        <span className="red-dot mt-[10px] shrink-0" />
                                        <span>{r}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* 09 — CLOSING */}
            <section
                id="closing"
                className="bg-white"
                data-testid="closing-section"
            >
                <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
                    <div className="grid grid-cols-12 gap-8">
                        <div className="col-span-12 lg:col-span-4">
                            <div className="flex items-center gap-3">
                                <span className="red-dot" />
                                <span className="section-num">
                                    09 — Closing
                                </span>
                            </div>
                        </div>
                        <div className="col-span-12 lg:col-span-8">
                            <h2 className="font-[800] tracking-[-0.03em] leading-[1.05] text-2xl sm:text-3xl lg:text-4xl">
                                The Commercial function moved from{" "}
                                <span className="bg-[var(--fora-red-1)] text-white px-2">
                                    foundational build
                                </span>{" "}
                                to{" "}
                                <span className="bg-[var(--fora-red-1)] text-white px-2">
                                    early activation
                                </span>
                                . May–July is the GTM delivery push.
                            </h2>
                            <p className="mt-6 text-base leading-relaxed text-[var(--fora-ink)]/80">
                                23 deliverables produced across Foundation and
                                Activation, with HubSpot Go-Live as the
                                centerpiece transition. The next quarter
                                operationalizes the build — rep execution,
                                market activation, and Q3 push readiness all
                                hinge on completing CRM go-live and unblocking
                                the paused investor materials.
                            </p>
                            <p className="mt-4 text-base leading-relaxed text-[var(--fora-ink)]/80">
                                Cross-team sync with{" "}
                                <strong>Product & IT</strong> on HubSpot ↔
                                Digital Onboarding integration and CCHBC
                                standardization is the highest-impact joint
                                workstream.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* 10 — SIGN-OFF */}
            <section
                className="bg-black text-white relative overflow-hidden border-t border-white/10"
                data-testid="signoff-section"
            >
                <div className="absolute inset-0 fora-pattern opacity-80" />
                <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-24">
                    <div className="grid grid-cols-12 gap-6 md:gap-10">
                        <div className="col-span-12 lg:col-span-5">
                            <div className="flex items-center gap-3">
                                <span className="red-dot" />
                                <span className="section-num text-white/80">
                                    10 — Sign-off
                                </span>
                            </div>
                            <h2 className="mt-5 font-[800] tracking-[-0.03em] leading-[1] text-4xl sm:text-5xl">
                                Prepared by the
                                <br />
                                Commercial team.
                            </h2>
                            <p className="mt-6 text-white/70 max-w-md">
                                For questions, deep-dives, or to request a
                                walkthrough of the GTM plan, reach out below.
                            </p>
                        </div>

                        <div className="col-span-12 lg:col-span-7 grid sm:grid-cols-2 gap-4">
                            <Person
                                name="Marko Erkić"
                                role="CBO · Chief Business Officer"
                                email="marko@master-fb.com"
                            />
                            <Person
                                name="Marko Pavičić"
                                role="RevOps Head"
                                email="marko.pavicic@master-fb.com"
                            />
                            <Person
                                name="Petar Kostić"
                                role="ExYu Sales Head"
                                email="petar.kostic@master-fb.com"
                            />
                            <Person
                                name="Ognjen Zlački"
                                role="Global Sales Head"
                                email="ognjen.zlacki@master-fb.com"
                            />
                            <Person
                                name="Srećko Knezović"
                                role="Partnership Head"
                                email="srecko.knezovic@master-fb.com"
                            />
                        </div>
                    </div>
                </div>
            </section>
        </ReportLayout>
    );
};

/* ---- Local helper for the build journal cards ---- */
const JournalGroup = ({ label, items }) => {
    if (!items || items.length === 0) return null;
    return (
        <div className="mt-5">
            <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[var(--fora-muted)] mb-2">
                {label}
            </div>
            <ul className="space-y-2">
                {items.map((it, i) => (
                    <li
                        key={i}
                        className="flex items-start justify-between gap-3 text-sm leading-snug border-l-2 border-[var(--fora-red-1)] pl-3"
                    >
                        <span className="text-[var(--fora-ink)]/85 flex-1">
                            {it.name}
                        </span>
                        <StatusPill status={it.status} />
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default BusinessReport;
